// ================================================================
// 命盤分享 API 模組
// ================================================================

import apiClient from './client';

// ================================================================
// 型別定義
// ================================================================

export type ShareField =
  | 'nickname'
  | 'birth_year'
  | 'gender'
  | 'chart_grid'
  | 'mingong_star'
  | 'big_limit';

export interface ShareFieldOption {
  key: ShareField;
  label: string;
  description: string;
}

export const SHARE_FIELD_OPTIONS: ShareFieldOption[] = [
  { key: 'nickname',    label: '姓名 / 代稱',       description: '顯示您的姓名或代稱' },
  { key: 'birth_year',  label: '出生年份',           description: '只顯示西元年，不含月日時辰' },
  { key: 'gender',      label: '性別',               description: '男命 / 女命' },
  { key: 'chart_grid',  label: '命盤格局圖',         description: '完整 4×4 十二宮位視覺化命盤' },
  { key: 'mingong_star', label: '命宮主星',          description: '命宮的主要星曜' },
  { key: 'big_limit',   label: '目前大限',           description: '目前所在大限宮位與年齡範圍' },
];

export type SharePurpose = 'personal' | 'friendship' | 'career' | 'romantic';

export interface SharePurposeOption {
  key: SharePurpose;
  label: string;
  icon: string;
  ctaLabel: string;
}

export const SHARE_PURPOSE_OPTIONS: SharePurposeOption[] = [
  { key: 'personal',   label: '個人資料分享', icon: '👤', ctaLabel: '' },
  { key: 'friendship', label: '交友配對',     icon: '🤝', ctaLabel: '進行免費交友配對分析' },
  { key: 'career',     label: '事業配對',     icon: '💼', ctaLabel: '進行免費事業配對分析' },
  { key: 'romantic',   label: '感情配對',     icon: '💕', ctaLabel: '進行免費感情配對分析' },
];

export interface ShareConfig {
  exists: boolean;
  share_token?: string;
  shared_fields?: ShareField[];
  share_purpose?: SharePurpose;
  is_active?: boolean;
  view_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ShareSaveResponse {
  success: boolean;
  share_token?: string;
  error?: string;
}

export interface ShareAccessResponse {
  success: boolean;
  shared_fields?: ShareField[];
  share_purpose?: SharePurpose;
  data?: ShareSnapshot;
  error?: string;
}

export interface ShareSnapshot {
  nickname?: string;
  birth_year?: string;
  gender_label?: string;
  mingong_star?: string;
  current_big_limit?: {
    palace_name: string;
    start_age: number;
    end_age: number;
  };
  chart_raw?: any[];  // rawData[0..18]，與 ChartPage.tsx 相同格式
}

// ================================================================
// API 函數
// ================================================================

export const saveShareConfig = async (
  password: string,
  sharedFields: ShareField[],
  sharePurpose: SharePurpose = 'personal'
): Promise<ShareSaveResponse> => {
  const response = await apiClient.post('/api/user/share/save', null, {
    params: { password, shared_fields: JSON.stringify(sharedFields), share_purpose: sharePurpose },
  });
  return response as unknown as ShareSaveResponse;
};

export const getShareConfig = async (): Promise<{ success: boolean; config: ShareConfig }> => {
  const response = await apiClient.get('/api/user/share/config');
  return response as unknown as { success: boolean; config: ShareConfig };
};

export const regenerateShare = async (
  password: string,
  sharedFields: ShareField[],
  sharePurpose: SharePurpose = 'personal'
): Promise<ShareSaveResponse> => {
  const response = await apiClient.post('/api/user/share/regenerate', null, {
    params: { password, shared_fields: JSON.stringify(sharedFields), share_purpose: sharePurpose },
  });
  return response as unknown as ShareSaveResponse;
};

export const revokeShare = async (): Promise<{ success: boolean }> => {
  const response = await apiClient.delete('/api/user/share/revoke');
  return response as unknown as { success: boolean };
};

export const accessShare = async (
  token: string,
  password: string
): Promise<ShareAccessResponse> => {
  const response = await apiClient.post(`/api/share/${token}/access`, null, {
    params: { password },
  });
  return response as unknown as ShareAccessResponse;
};
